import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { FC, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import {
    MediaAsset,
    MediaAssetCategory,
    colors,
} from '@hazadapt-git/public-core-base'
import { MediaAssetItem } from '../molecules'
import { theme, customBorderRadius } from '../../lib/styles/universal'

interface MediaAssetGridProps {
    assets: MediaAsset[]
    onViewAsset: (asset: MediaAsset) => void
    onDownloadAsset: (asset: MediaAsset) => Promise<void>
    isLoading?: boolean
}

export const MediaAssetGrid: FC<MediaAssetGridProps> = ({
    assets,
    onViewAsset,
    onDownloadAsset,
    isLoading = false,
}) => {
    const { classes } = useStyles()
    const [tab, setTab] = useState(0)

    const printables = assets.filter(
        (asset: MediaAsset) => asset.category === MediaAssetCategory.PRINTABLES
    )
    const branding = assets.filter(
        (asset: MediaAsset) => asset.category === MediaAssetCategory.BRANDING
    )
    const scannables = assets.filter(
        (asset: MediaAsset) => asset.category === MediaAssetCategory.SCANNABLES
    )

    const getCurrentAssets = () => {
        switch (tab) {
            case 0:
                return { assets: printables, name: 'Posters & Printables' }
            case 1:
                return { assets: branding, name: 'Logo & Branding' }
            case 2:
                return { assets: scannables, name: 'QR Codes' }
            default:
                return { assets: [], name: 'Assets' }
        }
    }

    const { assets: currentAssets, name: categoryName } = getCurrentAssets()

    const MediaAssetSkeleton = () => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                padding: '0.5rem',
                height: '100%',
                minWidth: '8rem',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    borderRadius: customBorderRadius,
                    overflow: 'hidden',
                    backgroundColor: colors.grays.SMOOTH,
                    border: `2px solid ${colors.primary.CERULEAN}`,
                    padding: '0.5rem',
                    minHeight: '12rem',
                    minWidth: '8rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Skeleton
                    variant="rectangular"
                    width="100%"
                    height="11rem"
                    animation="wave"
                />
            </Box>
            <Skeleton
                variant="text"
                height="2.5rem"
                animation="wave"
                sx={{ fontSize: '0.875rem' }}
            />
        </Box>
    )

    return (
        <Box className={classes.container}>
            <Typography variant="h3">Media Kit</Typography>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                className={classes.tabs}
                aria-label="Media Kit Tabs"
                sx={{ width: '100%' }}
            >
                <Tab label="Posters & Printables" />
                <Tab label="Logo & Branding" />
                <Tab label="QR Codes" />
            </Tabs>
            <Box className={classes.tabPanel}>
                <Grid container spacing={3} mt={1} sx={{ width: '100%' }}>
                    {isLoading ? (
                        [1, 2, 3].map((index) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                                key={index}
                                sx={{
                                    minWidth: 0,
                                    height: 'fit-content',
                                    display: 'flex',
                                }}
                            >
                                <MediaAssetSkeleton />
                            </Grid>
                        ))
                    ) : currentAssets.length !== 0 ? (
                        currentAssets.map((asset: MediaAsset) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={3}
                                lg={2}
                                key={asset.display_name}
                                sx={{
                                    minWidth: 0,
                                    height: 'fit-content',
                                    display: 'flex',
                                }}
                            >
                                <MediaAssetItem
                                    asset={asset}
                                    onView={onViewAsset}
                                    onDownload={onDownloadAsset}
                                />
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box className={classes.emptyState}>
                                <Typography variant="h6" color="text.secondary">
                                    Your Organization doesn't currently have{' '}
                                    {categoryName}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    )
}

const useStyles = makeStyles()({
    container: {
        width: '100%',
        marginTop: '2.5rem',
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        width: '100%',
    },
    tabPanel: {
        minHeight: '20rem',
        width: '100%',
        maxWidth: '100%',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '20rem',
        width: '100%',
        padding: '2rem',
        textAlign: 'center',
    },
})
